/**
 * CurrencyFieldType — kwota w PLN/EUR/USD. Storage: PG NUMERIC(15,2).
 *
 * MVP: zakładamy PLN domyślnie. Multi-currency support w późniejszej fazie.
 */

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

function CurrencyInput({ field, value, onChange, error, disabled, autoFocus }: FieldInputProps) {
  const stringValue =
    typeof value === 'number' ? value.toFixed(2) : typeof value === 'string' ? value : '';
  return (
    <div className="relative">
      <input
        type="number"
        step="0.01"
        value={stringValue}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') onChange(null);
          else {
            const n = parseFloat(raw);
            onChange(Number.isFinite(n) ? n : null);
          }
        }}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-invalid={!!error}
        data-testid={`field-${field.slug}-input`}
        className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 pr-12 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">
        PLN
      </span>
    </div>
  );
}

export const CurrencyFieldType: FieldTypeDefinition = {
  type: 'currency',
  renderInput: (props) => <CurrencyInput {...props} />,
  renderCell: (_field, value) => {
    // Postgres NUMERIC zwraca przez pg driver jako string — parseFloat dla obu wariantów.
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : '';
  },
  validate: (value) => {
    if (value === null || value === undefined || value === '') return null;
    // Postgres NUMERIC zwraca przez pg driver jako string — akceptuj oba warianty.
    // Renderer (CurrencyInput) trzyma value jako number gdy user edytuje, ale przy
    // ładowaniu z bazy może być stringiem "320.00".
    const n =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? parseFloat(value.replace(',', '.'))
          : Number.NaN;
    if (!Number.isFinite(n)) return 'invalid_currency';
    if (n < 0) return 'negative_currency';
    return null;
  },
  defaultValue: null,
};
